import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {findParentTags, findTagChildren} from '../../../../helpers/models/tagHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {TagBehaviorsEnum} from '../../../../models/api/tagModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceTag} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {TagModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {TagCreatedHandler, ToggleDropdownTagHandler} from '../editor/context/workspacesEditorContext';
import {PickTagsDropdown, ToggleTagHandler} from './pickTagsDropdown';

/*
 * Props.
 */

interface PickTagsDropdownContainerProps {
  team?: TeamModel;
  behaviors?: ReadonlyArray<TagBehaviorsEnum>;
  onToggleTag: ToggleDropdownTagHandler;
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>;
  tagModelMap: TagModelMap;
  anchor: Anchor;
  onRequestClose: Handler;
  onTagCreated?: TagCreatedHandler;
}

/*
 * Selectors.
 */

function getTeam(_state: AppState, props: PickTagsDropdownContainerProps) {
  return props.team;
}

function makeGetNamespace() {
  return createSelector([getTeam, getUserNamespace], (team, userNamespace) =>
    team ? team.namespace : userNamespace,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PickTagsDropdownContainerProps>()(() => {
  const getNamespace = makeGetNamespace();
  return {
    company: getCompany,
    namespace: getNamespace,
    tags: getTags,
  };
});

/**
 * This component is adapted from the now-removed <PickTagsDropdownContainer /> but for use in <WorkspacesEditor />.
 * This is specifically for both personal and team tags in the personal workspace.
 */
export const WorkspacesEditorPickTagsDropdownContainer = connect(
  mapStateToProps,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const {tags} = stateProps;
    const {visualWorkspaceTags, tagModelMap} = ownProps;

    const tagIds = visualWorkspaceTags.map(({id}) => id);

    const toggleTag: ToggleTagHandler = (searchTag, saveGate) => {
      const combinedTags = _(searchTag)
        .filter((t) => !tags[t.id])
        .keyBy((t) => t.id)
        .assign(tags)
        .merge(tagModelMap)
        .value();

      const parentTagIdsToAdd = findParentTags(combinedTags, searchTag[0])
        .map(({id}) => id)
        .filter((id) => !tagIds.includes(id));
      const childTagIdsToRemove = findTagChildren(combinedTags, searchTag[0], false).map(({id}) => id);

      ownProps.onToggleTag(searchTag, parentTagIdsToAdd, childTagIdsToRemove);
    };

    return {
      ...stateProps,
      ...ownProps,
      toggleTag,
      visibleCustomViewIds: tagIds,
      isEditingWorkspace: true,
      shouldDisplayAddButton: true,
    };
  },
)(PickTagsDropdown);
